<template>
  <div v-if="reservation !== null">

    <p v-if="chatRoom === null">
      {{ $t('loading') }}...
    </p>
    <div class="room" v-else>
      <SocketIoChat :room="chatRoom" :client="client"/>
    </div>

    <div class="alert alert-danger mt-4" v-if="tyrionResponseStatus && tyrionResponseStatus.status === 'error'">
      {{ tyrionResponseStatus.msg }}
    </div>
  </div>
  <div v-else-if="reservations !== null">
    <div class="existing_reservations p-3 bg-silver radius-07 mb-3" v-if="reservations.length > 0">
      <p class="h4">{{ $t('currently-open-reservations') }}</p>
      <ul class="list-group">
        <li class="list-group-item" v-for="reservation in reservations">
          <div class="row">
            <div class="col-3">
              <figure class="pointer radius-07 m-0">
                <picture v-if="reservation.type === 'sub' && reservationUnitImage(reservation)">
                  <PartsSrcSetImage :image="reservationUnitImage(reservation)" :t_type="'thumbw'" :type="'webp'"/>
                  <PartsSrcSetImage :image="reservationUnitImage(reservation)" :t_type="'thumbw'" :type="'jpeg'"/>

                  <img :src="reservationUnitImage(reservation).getTyrionSrc('thumbw')"
                       :alt="reservation.reservedUnits.map(u => u.title).join(', ')"
                       class="img-cover radius-07" loading="lazy"
                       :width="reservationUnitImage(reservation).seoWidth('thumb')"
                       :height="reservationUnitImage(reservation).seoHeight('thumb')">
                </picture>
                <picture v-else>
                  <PartsSrcSetImage v-if="accommodation.hasImage(false)" :image="accommodation.getTopImage()"
                                    :t_type="'thumbw'"
                                    :type="'webp'"/>
                  <PartsSrcSetImage v-if="accommodation.hasImage(false)" :image="accommodation.getTopImage()"
                                    :t_type="'thumbw'"
                                    :type="'jpeg'"/>

                  <img v-if="accommodation.hasImage(false)" :src="accommodation.getTopImage().getTyrionSrc('thumbw')"
                       :alt="accommodation.title"
                       class="img-cover radius-07" loading="lazy"
                       :width="accommodation.getTopImage().seoWidth('thumb')"
                       :height="accommodation.getTopImage().seoHeight('thumb')">
                  <img v-else src="~/assets/img/blank.jpg" :alt="accommodation.title" class="img-cover radius-1-2"
                       loading="lazy"
                       width="600" height="337">
                </picture>
              </figure>


            </div>
            <div class="col-9">
              <small v-if="reservation.type === 'sub'"
                     class="d-block">{{ reservation.reservedUnits.map(u => u.title).join(', ') }}</small>
              <span v-else class="d-block">
                {{ $t('whole-object') }}
              </span>
              <span class="d-block">{{
                  formatTime(reservation.date_from, 'DD.MM.YYYY')
                }} - {{ formatTime(reservation.date_to, 'DD.MM.YYYY') }}</span>
              <button class=" btn btn-xs btn-outline-first btn-rounded me-2" type="button"
                      v-if="reservation.chat_room.length > 0" @click="showReservationChat(reservation)">
                {{ $t('open-chat') }}
              </button>
              <NuxtLink class=" btn btn-xs btn-outline-first btn-rounded" type="button" target="_blank"
                        :to="`/${activeLocale.code}/admin/trips?id_reservation=${reservation.id}`">
                {{ $t('show-detail') }}
              </NuxtLink>
            </div>
          </div>
        </li>
      </ul>

    </div>

    <p class="h5" v-if="reservationData.last_minute">{{ $t('reservation-of-lastminute-offer') }}</p>
    <p v-else>{{ $t('you-are-about-to-create-a-new-reservation') }}</p>

    <table class="table">
      <tbody>
      <tr>
        <td class="ps-0"><strong class="me-1">{{ $t('accommodation') }}:</strong></td>
        <td>{{ accommodation.title }}</td>
      </tr>
      <tr v-if="!accommodation.reserve_whole_object">
        <td class="ps-0"><strong class="me-1">{{ $t('objects') }}:</strong></td>
        <td>
          <span v-for="unit in selectedUnits">
            {{ unit.title }}
          </span>
        </td>
      </tr>
      <tr>
        <td class="ps-0"><strong class="me-1">{{ $t('guests') }}:</strong></td>
        <td>
          <span v-if="reservationData.guests.adult > 0" class="me-2">{{ reservationData.guests.adult }}x {{
              $t('adults')
            }}</span>
          <span v-if="reservationData.guests.child > 0" class="me-2">{{
              reservationData.guests.child
            }}x {{ $t('childrens') }}</span>
          <span v-if="reservationData.guests.newborn > 0" class="me-2">{{
              reservationData.guests.newborn
            }}x {{ $t('newborns') }}</span>
        </td>
      </tr>
      <tr>
        <td class="ps-0"><strong class="me-1">{{ $t('date') }}:</strong></td>
        <td>
          <span><strong>{{ $t('from') }}:</strong>&nbsp;{{ reservationData.date.from }}</span>
          <br>
          <strong>{{ $t('to') }}:</strong>&nbsp;<span>{{ reservationData.date.to }}</span>
          <br>
          {{ reservationDateDurations() }} {{ $t('nights') }}
        </td>
      </tr>
      <tr v-if="totalPrice > 0">
        <td class="ps-0"><strong class="me-1">{{ $t('total-price') }}:</strong></td>
        <td v-if="reservationData.last_minute">
          {{
            Math.round(reservationData.last_minute.curr_prices[currency.code] * reservationData.last_minute.durationInDays() * 100) / 100
          }}
          <del>{{ totalPrice }}</del>&nbsp;{{ currency.mark }}
        </td>
        <td v-else>
          {{ totalPrice }}&nbsp;{{ currency.mark }}
          <span v-if="activeLocaleCurrency.code != accommodation.main_currency && totalMainCurrencyPrice > 0">
            ({{ totalMainCurrencyPrice }} {{ switchCurrencyMark(accommodation.main_currency, activeLocale.code) }})
          </span>
        </td>
      </tr>
      <tr v-if="totalPrice > 0">
        <td class="ps-0"><strong class="me-1">{{ $t('price-per-night') }}:</strong></td>
        <td v-if="reservationData.last_minute">
          {{ Math.round(reservationData.last_minute.curr_prices[currency.code] * 100) / 100 }}
          <del>{{ pricePerNight }}</del>&nbsp;{{ currency.mark }}
        </td>
        <td v-else>
          {{ pricePerNight }}&nbsp;{{ currency.mark }}
        </td>
      </tr>
      </tbody>
    </table>


    <div class="form-group">
      <label for="additional_note" class="fw-bold mb-1 pointer">{{ $t('note-for-owner') }}:</label>
      <textarea required name="additional_note" id="additional_note" class="form-control mb-3"
                v-model="reservationData.note" rows="6"></textarea>
    </div>

    <div class="alert alert-danger" v-if="alreadyReserved">
      {{ $t('offer-is-already-reserved') }}
    </div>
    <button class="btn btn-first btn-rounded d-block w-100" @click="confirmReservation"
            :disabled="submitting || alreadyReserved">
      {{ $t('confirm-and-reserve') }}
    </button>

    <p class="mt-3 mb-1"><strong>{{ $t('what-happen-after-reservation') }}</strong></p>
    <p class="small">{{ $t('what-happen-after-reservation-answer') }}</p>

    <PartsTempPreloader v-if="submitting"/>
    <div class="alert alert-danger mt-4" v-if="tyrionResponseStatus && tyrionResponseStatus.status === 'error'">
      {{ tyrionResponseStatus.msg }}
    </div>
  </div>
  <div v-else>
    {{ $t('loading') }}...
  </div>
</template>

<script setup lang="ts">

import {computed, onMounted, onUnmounted, PropType, ref} from "vue";
import Accommodation from "~/lib/models/Accommodation";
import Client from "~/lib/models/Client";
import {tyrionResponse} from "~/composables/tyrionResponse";
import SocketIoChat from "~/components/admin/chat/messenger/small_chat";
import dayjs from "dayjs";
import AccommodationReservation from "~/lib/models/AccommodationReservation";
import {formatTime, switchCurrencyMark} from "~/lib/functions";
import {obtainActiveLocale, obtainActiveLocaleCurrency} from "~/composables/master_page";
import {useNuxtApp} from "#app";
import {GATracker} from "../../../../../../composables/ga_tracker";

const {setTyrionResponseStatus, tyrionResponseStatus} = tyrionResponse();
const {GATrackEvent} = GATracker();
const props = defineProps({
  accommodation: Object as PropType<Accommodation>,
  client: Object as PropType<Client>,
  reservationData: Object,
  currency: Object as PropType<{ code: String, mark: String }>
})

const emit = defineEmits(['afterReservationAction']);

const chatRoom = ref(null);
const reservations = ref(null);
const reservation = ref<AccommodationReservation | null>(null);
const checker_interval = ref<null | number>(null);
const totalPrice = ref(null);
const totalMainCurrencyPrice = ref(null);
const submitting = ref(false);
const alreadyReserved = ref(false);
const activeLocale = obtainActiveLocale();
const activeLocaleCurrency = obtainActiveLocaleCurrency();
const {$tyrionApi, $socketApi} = useNuxtApp();

const selectedUnits = computed(() => {
  return props.accommodation.units.filter(unit => {
    return props.reservationData.id_units.indexOf(unit.id) > -1
  })
});
const pricePerNight = computed(() => {
  return !isNaN(totalPrice.value) && dateRangeDuration.value > 0 ? (Math.round(totalPrice.value / dateRangeDuration.value)) : -1;
});
const dateRangeDuration = computed(() => {
  if (props.reservationData.date.from && props.reservationData.date.to) {
    return Number(dayjs(props.reservationData.date.to).diff(dayjs(props.reservationData.date.from), 'day'));
  }
  return 0;
});

onMounted(() => {
  loadReservations();
  loadPrice();
  initReservationFreeChecker();
})

onUnmounted(() => {
  if (checker_interval.value !== null) {
    clearInterval(checker_interval.value);
  }
})

const loadReservations = () => {
  return $tyrionApi.postTyrionUrl('/api3/reservations/obtain_temp', {
    body: {filter: {id_accommodation: props.accommodation.id}},
    userTokenRequired: true
  }).then((res) => {
    if (res.status === 'success') {
      reservations.value = res.data.map(res => (new AccommodationReservation()).fromObject(res));
    }
    return true;
  })
};
const loadPrice = () => {
  $tyrionApi.postTyrionUrl('/api3/accommodation/trip-price', {
    body: {
      id_accommodation: props.accommodation.id,
      id_unit: selectedUnits.value.map(u => u.id),
      dateFrom: props.reservationData.date.from,
      dateTo: props.reservationData.date.to,
      guests: props.reservationData.guests,
      currency: activeLocaleCurrency.code
    }
  }).then(res => {
    totalPrice.value = 0;
    totalMainCurrencyPrice.value = 0;
    if (res.data[props.accommodation.main_currency] !== undefined) {
      Object.keys(res.data[props.accommodation.main_currency]).forEach(idUnit => {
        totalPrice.value += res.data[idUnit].price
      });
    }
    if (res.data[activeLocaleCurrency.code] !== undefined) {
      Object.keys(res.data[activeLocaleCurrency.code]).forEach(idUnit => {
        totalPrice.value += res.data[idUnit].price
      });
    }

  })
};
const confirmReservation = () => {
  if (submitting.value === false) {
    submitting.value = true;
    const reservation_data = {...{id_accommodation: props.accommodation.id}, ...props.reservationData};
    reservation_data.last_minute = reservation_data.last_minute ? reservation_data.last_minute.id : null;
    $tyrionApi.postTyrionUrl('/api3/reservations/create_temp', {
      body: {
        reservation_data: reservation_data,
        lang: activeLocale.code
      },
      userTokenRequired: true
    }).then((res) => {
      if (res.status === 'success') {
        const createdReservation = res.data; //  as {id:number, chat_room: string};
        loadReservations().then(() => {
          if (reservations.value.some(r => r.id == createdReservation.id)) {
            reservation.value = reservations.value.find(r => r.id == createdReservation.id);
            initAccommodationChat(createdReservation.chat_room);

            emit('afterReservationAction', reservation.value);
          }

          GATrackEvent({
            event: 'purchase',
            language: activeLocale.code,
            transaction_id: createdReservation.id,
            currency: props.currency.code,
            tax: '20',
            action: 'create',
            label: 'Create temp reservation',
            value: totalPrice,
            items: [
              {
                item_id: props.accommodation.id,
                item_name: props.accommodation.title,
                item_category: props.accommodation.category,
                quantity: dateRangeDuration.value,
                price: pricePerNight.value,
                currency: props.currency.code,
                date_from: props.reservationData.date.from,
                guest_count: Number(reservation_data.guests.adult) + Number(reservation_data.guests.child) + Number(reservation_data.guests.newborn),
                item_variant: props.accommodation.location.tourist_region.code
              }
            ]
          })

          submitting.value = false;
        })
        reservations.value = res.data;
      } else {
        setTyrionResponseStatus('error', res.message);
        submitting.value = false;
      }
    }).catch((e) => {
      setTyrionResponseStatus('error', (e.response && e.response.data && e.response.data.message) ? e.response.data.message : `Chat error: ${e.message}`);
      submitting.value = false;
    })
  }

};

const showReservationChat = (tReservation: AccommodationReservation) => {
  reservation.value = tReservation;
  initAccommodationChat(tReservation.chat_room);
};
const initAccommodationChat = (code: string) => {
  $socketApi.postSocketUrl("/api/chat/rooms", {
    body: {
      code: code,
      recipients: [props.accommodation.owner.id],
      subject: 'NO NEEDED'
    }
  }).then((res) => {
    if (res.status === 'success') {
      chatRoom.value = res.data;
    } else {
      setTyrionResponseStatus('error', res.message);
    }
  }).catch((e) => {
    setTyrionResponseStatus('error', (e.response && e.response.data && e.response.data.message) ? e.response.data.message : `Chat error: ${e.message}`);
  })
};

const reservationUnitImage = (reservation) => {
  const images = [];
  if (reservation.type === 'sub' && reservation.units && reservation.units.length > 0) {
    reservation.units.forEach(unit => {
      if (unit.hasImage(false)) {
        images.push(unit.getTopImage());
      }
    })
  }
  return images.length > 0 ? images[0] : null;
};

const initReservationFreeChecker = () => {
  checker_interval.value = setInterval(() => {
    if (reservation.value) {
      clearInterval(checker_interval.value);
      checker_interval.value = null;
    } else {
      $tyrionApi.postTyrionUrl("/api3/reservations/check_availability", {
        body: {
          dates: props.reservationData.date,
          last_minute: props.reservationData.last_minute ? props.reservationData.last_minute.id : null,
          id_accommodation: props.accommodation.id,
          id_units: props.reservationData.id_units
        }
      }).then(res => {
        if (res.data.reserved === true) {
          alreadyReserved.value = true;
          clearInterval(checker_interval.value);
          checker_interval.value = null;
        }
      })
    }
  }, 2000);
};

const reservationDateDurations = () => {
  if (props.reservationData && props.reservationData.date && props.reservationData.date.from) {
    const dateFrom = dayjs(props.reservationData.date.from).minute(0).hour(0).second(0).millisecond(0);
    const dateTo = dayjs(props.reservationData.date.to).minute(0).hour(0).second(0).millisecond(0);
    return dateTo.diff(dateFrom, 'day')
  } else {
    return 0;
  }
}

</script>


